/**
 * Owns the count-session hook + full-screen shell so the Inventory
 * panel orchestrator stays under the file-size hygiene cap.
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { ManufacturingInventoryCountCloseConfirm } from "./ManufacturingInventoryCountCloseConfirm";
import { ManufacturingInventoryCountDiscardConfirm } from "./ManufacturingInventoryCountDiscardConfirm";
import { ManufacturingInventoryCountShell } from "./ManufacturingInventoryCountShell";
import { useManufacturingInventoryCount } from "./useManufacturingInventoryCount";
import { buildInventoryCountClosePreview } from "@/lib/manufacturing/inventoryCountClosePreview";
import {
  buildInventoryCountOfflineDraft,
  bulkInvDownloadFilename,
  downloadInventoryCountOfflineDraft,
} from "@/lib/manufacturing/inventoryCountOfflineDraft";

type Props = {
  open: boolean;
  enabled: boolean;
  plantId?: string;
  canReviewProposals: boolean;
  onClose: () => void;
  /** Called after an approved proposal creates a material (refresh catalog). */
  onMaterialsChanged?: () => void;
  /** Optional toast after close reconciliation. */
  onSessionClosed?: (summary: string) => void;
};

export function ManufacturingInventoryCountHost({
  open,
  enabled,
  plantId,
  canReviewProposals,
  onClose,
  onMaterialsChanged,
  onSessionClosed,
}: Props) {
  const count = useManufacturingInventoryCount({ enabled, plantId });
  const countRef = useRef(count);
  countRef.current = count;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  const closePreview = useMemo(
    () => buildInventoryCountClosePreview(count.lines),
    [count.lines],
  );

  const onMountOpen = useCallback(() => {
    void (async () => {
      const c = countRef.current;
      const r = await c.resumeOrNull();
      if (r.ok && !r.session) await c.open();
    })();
  }, []);

  const handleExit = useCallback(() => {
    setConfirmOpen(false);
    setDiscardOpen(false);
    countRef.current.reset();
    onClose();
  }, [onClose]);

  const handleRequestCloseSession = useCallback(() => {
    setConfirmOpen(true);
  }, []);

  const handleRequestDiscard = useCallback(() => {
    setDiscardOpen(true);
  }, []);

  const handleDownloadDraft = useCallback(() => {
    const c = countRef.current;
    if (!c.session) return;
    const draft = buildInventoryCountOfflineDraft({
      organizationId: c.session.organization_id,
      plantId: plantId || c.session.plant_id,
      session: c.session,
      lines: c.lines,
    });
    downloadInventoryCountOfflineDraft(draft, bulkInvDownloadFilename());
  }, [plantId]);

  const handleAcceptClose = useCallback(() => {
    void (async () => {
      const r = await countRef.current.close();
      if (r.ok) {
        const recon = r.reconciliation;
        if (recon && onSessionClosed) {
          const fail = recon.failed.length;
          onSessionClosed(
            fail > 0
              ? `Count closed: ${recon.adjustments_posted} adjustments, ${fail} failed`
              : `Count closed: ${recon.adjustments_posted} adjustment${
                  recon.adjustments_posted === 1 ? "" : "s"
                } posted`,
          );
        }
        setConfirmOpen(false);
        countRef.current.reset();
        onMaterialsChanged?.();
        onClose();
      }
    })();
  }, [onClose, onMaterialsChanged, onSessionClosed]);

  const handleAcceptDiscard = useCallback(() => {
    void (async () => {
      const r = await countRef.current.archive();
      if (r.ok) {
        onSessionClosed?.("Count archived — no stock adjustments posted");
        setDiscardOpen(false);
        countRef.current.reset();
        onClose();
      }
    })();
  }, [onClose, onSessionClosed]);

  return (
    <>
      <ManufacturingInventoryCountShell
        open={open}
        enabled={enabled}
        busy={count.busy}
        error={count.error}
        session={count.session}
        lines={count.lines}
        proposals={count.proposals}
        savingLineId={count.savingLineId}
        proposing={count.proposing}
        reviewingId={count.reviewingId}
        canReviewProposals={canReviewProposals}
        onOpenSession={() => void count.open()}
        onCloseSession={handleRequestCloseSession}
        onDiscardSession={handleRequestDiscard}
        onExit={handleExit}
        onMountOpen={onMountOpen}
        onCommitQty={(lineId, qty) => void count.setLineQty(lineId, qty)}
        onProposeNewItem={(opts) => void count.proposeNewItem(opts)}
        onApproveProposal={(id) => {
          void (async () => {
            const r = await count.reviewProposal(id, "approve");
            if (r.ok) onMaterialsChanged?.();
          })();
        }}
        onRejectProposal={(id) => void count.reviewProposal(id, "reject")}
        onRefreshProposals={() => {
          if (count.session) void count.loadProposals(count.session.id);
        }}
        onDownloadDraft={handleDownloadDraft}
      />
      <ManufacturingInventoryCountCloseConfirm
        open={confirmOpen}
        busy={count.busy}
        preview={closePreview}
        onCancel={() => setConfirmOpen(false)}
        onAccept={handleAcceptClose}
      />
      <ManufacturingInventoryCountDiscardConfirm
        open={discardOpen}
        busy={count.busy}
        counted={count.lines.filter((l) => l.counted_qty != null).length}
        total={count.lines.length}
        onCancel={() => setDiscardOpen(false)}
        onAccept={handleAcceptDiscard}
      />
    </>
  );
}
